import { useState } from "react";
import type { InputType } from "../../services/inputTypesApi";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MoreHorizontal, Search, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useDeactivateInputType } from "../../hooks/useInputTypes";
import { EditInputTypeDialog } from "./EditInputTypeDialog";
import { useConfirm, withConfirmSuccess } from "@/shared/contexts/confirm-context";

interface InputTypesTableProps {
  inputTypes: InputType[];
}

export const InputTypesTable = ({ inputTypes }: InputTypesTableProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const deleteMutation = useDeactivateInputType();
  const confirm = useConfirm();

  const filteredInputTypes = inputTypes.filter(
    (type) =>
      type.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      type.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDelete = (id: string, name: string) => {
    confirm({
      title: "Deactivate input type?",
      description: `“${name}” will be deactivated and hidden from new inputs.`,
      variant: "destructive",
      confirmLabel: "Deactivate",
      onConfirm: () =>
        withConfirmSuccess(deleteMutation.mutateAsync(id), `${name} deactivated`),
    });
  };

  return (
    <div className="space-y-4">
      <div className="relative w-64 ml-auto">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <Input
          placeholder="Search input types..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      <div className="rounded-lg border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredInputTypes.map((type) => (
              <TableRow key={type.id}>
                <TableCell className="font-medium text-sm">
                  {type.code}
                </TableCell>
                <TableCell className="font-medium">{type.name}</TableCell>
                <TableCell className="text-sm text-gray-600 break-words max-w-xs">
                  {type.description}
                </TableCell>
                <TableCell>
                  <Badge variant={type.isActive ? "default" : "destructive"}>
                    {type.isActive ? "Active" : "Inactive"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      {type.isActive && (
                        <DropdownMenuItem asChild>
                          <EditInputTypeDialog inputType={type} />
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        className="text-red-600"
                        onClick={() => handleDelete(type.id, type.name)}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Deactivate
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {filteredInputTypes.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No input types found
        </div>
      )}
    </div>
  );
};